// Reference data (payment methods, countries, currencies). All idempotent
// reads — cached aggressively at the hook layer (5+ min stale time).
import { apiClient, endpoints, unwrap } from '@core/api';
import { getActiveChannelId } from '@core/store/storeContextStore';

/** @returns {Promise<import('@core/types').PaymentMethod[]>} */
export async function listPaymentMethods() {
  const channelId = getActiveChannelId();
  const raw = await apiClient.get(endpoints.account.paymentMethods, {
    query: { account_platform_id: channelId || undefined },
  });
  const { data } = unwrap(raw);
  const list = Array.isArray(data) ? data : [];
  return list.map((m) => ({
    id: m.payment_method_id ?? m.id,
    name: m.name ?? '',
    code: m.code ?? undefined,
    slug: m.slug ?? m.payment_method_value?.slug ?? m.payment_method?.slug ?? undefined,
    isActive: m.is_active ?? true,
  }));
}

export async function listCountries() {
  const raw = await apiClient.get(endpoints.reference.countries);
  const { data } = unwrap(raw);
  return Array.isArray(data) ? data : [];
}

// States of a country and cities of a state, the same lookups the admin's
// customer form uses. The backend also accepts the names as query params.
export async function listStates(countryCode, { countryName } = {}) {
  if (!countryCode) return [];
  const raw = await apiClient.get(endpoints.reference.country(countryCode), {
    query: countryName ? { country: countryName, countryname: countryName } : undefined,
  });
  const { data } = unwrap(raw);
  return Array.isArray(data) ? data : [];
}

export async function listCities(countryCode, stateCode, { countryName, stateName } = {}) {
  if (!countryCode || !stateCode) return [];
  const raw = await apiClient.get(endpoints.reference.countryStates(countryCode, stateCode), {
    query: {
      country: countryName || undefined,
      countryname: countryName || undefined,
      state: stateName || undefined,
      statename: stateName || undefined,
    },
  });
  const { data } = unwrap(raw);
  return Array.isArray(data) ? data : [];
}

export async function listCurrencies() {
  const raw = await apiClient.get(endpoints.reference.currencies);
  const { data } = unwrap(raw);
  return Array.isArray(data) ? data : [];
}
